<script setup>
import { reactive, ref } from "vue";
import { useRouter } from "vue-router";

import QuestionsComponent from "./QuestionsComponent.vue";
import NameAndFilesComponent from "./NameAndFilesComponent.vue";
import SuccessModal from "./SuccessModal.vue";
import api from "@/api/index.js";
import { useDefaultStore } from "@/store";

const router = useRouter();

const store = useDefaultStore();

const turnstile_token = computed(() => store.getTurnstyleToken);
const form = reactive({
	name: "",
	email: "",
	phone: "",
	cv: null,
	task: null,

	questions: "",
	experience: "",
	build: "",
	rules: false,
	self_withdrawl: false,
	i_understand_course: false,
	no_outside_help: false,
});

const loading = ref(false);
const showSubmitError = ref(false);
const submitErrorMessage = ref("");
const showSuccessModal = ref(false);

const nameAndFilesRef = ref(null);
const questionsRef = ref(null);

async function submitForm() {
	if (!turnstile_token.value) {
		return;
	}
	nameAndFilesRef.value?.validateAll();
	questionsRef.value?.validateAll();

	const allValid =
		nameAndFilesRef.value?.isValid && questionsRef.value?.isValid;

	if (!allValid) {
		showSubmitError.value = true;

		if (nameAndFilesRef.value?.emailInvalid) {
			submitErrorMessage.value = "Please enter a valid email address.";
		} else {
			submitErrorMessage.value = "Please fill out all mandatory fields.";
		}

		return;
	}

	showSubmitError.value = false;
	submitErrorMessage.value = "";

	const formData = new FormData();

	formData.append("name", form.name);
	formData.append("email", form.email);
	formData.append("phone", form.phone);
	formData.append("questions", form.questions);
	formData.append("experience", form.experience);
	formData.append("build", form.build);
	formData.append("rules", form.rules ? "1" : "0");
	formData.append("token", turnstile_token.value);
	formData.append("i_understand_course", form.i_understand_course ? "1" : "0");
	formData.append("self_withdrawl", form.self_withdrawl ? "1" : "0");
	formData.append("no_outside_help", form.no_outside_help ? "1" : "0");

	if (form.cv) {
		formData.append("cv", form.cv);
	}

	if (form.task) {
		formData.append("test", form.task);
	}

	try {
		showSuccessModal.value = true;
		loading.value = true;

		await api.postApplication(formData);

		loading.value = false;
	} catch (error) {
		showSuccessModal.value = false;
		console.error("Failed to send application:", error);
	} finally {
		loading.value = false;
		store.clearTurnstyleToken();
	}
}

const buttonDisabled = computed(() => {
	return loading.value || !turnstile_token.value;
});

const buttonText = computed(() => {
	if (loading.value) {
		return "Sending...";
	}
	if (!turnstile_token.value) {
		return "Initializing";
	}
	return "Submit application";
});

function handleCloseModal() {
	showSuccessModal.value = false;
	router.push("/");
}
</script>

<template>
	<form class="form" novalidate @submit.prevent="submitForm">
		<NameAndFilesComponent ref="nameAndFilesRef" v-model="form" />

		<QuestionsComponent ref="questionsRef" v-model="form" />

		<div class="submit-row">
			<button type="submit" class="submit-btn" :disabled="buttonDisabled">
				{{ buttonText }}
			</button>

			<span v-if="showSubmitError" class="submit-error">
				{{ submitErrorMessage }}
			</span>
		</div>
	</form>

	<SuccessModal
		v-if="showSuccessModal"
		:loading="loading"
		@close-modal="handleCloseModal"
	>
		<div class="submit-modal-content">
			<h2 class="submit-modal-title">Application submitted!</h2>

			<p class="submit-modal-text">Thank you for applying to our course!</p>
			<p class="submit-modal-text">
				Your application has been successfully received. We appreciate your
				interest in joining our course program.
			</p>
			<p class="submit-modal-text">
				Our team will review all submitted applications, and if your profile
				matches the program requirements, we will contact you with information
				about the next steps.
			</p>
			<p class="submit-modal-text">
				We wish you the best of luck, and thank you for your trust in us!
			</p>
		</div>
	</SuccessModal>
</template>

<style lang="scss" scoped>
@use "@/assets/css/vars" as *;

.form {
	display: flex;
	flex-direction: column;
	align-items: center;
	background-color: $background-color-f5;
	gap: 64px;
	width: 100%;
	padding: 80px;
}

.submit-row {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 8px;
	width: 100%;
	max-width: 1100px;
	margin: 0 auto;
}

.submit-error {
	width: 100%;
	font-family: $font-main;
	font-weight: $font-medium;
	font-size: $text-sm;
	line-height: 150%;
	color: #d92d20;
}

.submit-btn {
	height: $button-height;
	border-radius: $button-border-radius;
	background: $app-color;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0 35px;
	color: $text-white;
	font-family: $font-main;
	font-weight: $font-semibold;
	font-size: $text-md;
	border: none;
	cursor: pointer;
	transition: opacity 0.2s ease;
	&:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}

	&:hover:not(:disabled) {
		opacity: 0.9;
	}
}

.submit-modal-content {
	display: flex;
	flex-direction: column;
	justify-content: center;
	text-align: center;
	gap: 16px;
	padding: 24px 0;
}

.submit-modal-title {
	margin: 0;
	font-family: $font-heading;
	font-weight: $font-bold;
	font-size: $text-xl;
	color: $app-color;
}

.submit-modal-text {
	margin: 0;
	font-family: $font-main;
	font-weight: $font-regular;
	font-size: $text-base;
	line-height: 150%;
	color: $text-color;
}

@media screen and (max-width: 1200px) {
	.form {
		padding: 60px 80px;
	}
}

@media screen and (max-width: 768px) {
	.form {
		gap: 40px;
		padding: 40px 24px;
	}
}

@media screen and (max-width: 480px) {
	.form {
		padding: 32px 16px;
	}

	.submit-btn {
		width: 100%;
	}
}
</style>
